import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import { toast } from 'sonner';
import { 
  Calendar, 
  Search,
  LogOut,
  Home,
  Mail,
  Phone,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import AdminLayout from '../../components/layout/AdminLayout';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminBookings() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/bookings`, { withCredentials: true });
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/bookings/${bookingId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(`Booking ${newStatus}`);
      fetchBookings();
    } catch (error) {
      console.error('Error updating booking:', error);
      toast.error('Failed to update booking');
    }
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = booking.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          booking.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || booking.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-[#FFF7ED] text-[#F97316]',
      'confirmed': 'bg-green-100 text-green-700',
      'completed': 'bg-[#EFF6FF] text-[#1D4ED8]',
      'cancelled': 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-[#F1F5F9] text-[#475569]';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <AdminLayout title="Bookings"><div>
        <div className="flex items-center justify-between mb-8">
          <h1 className="heading-2 text-2xl text-[#0A0F1C]" data-testid="bookings-title">
            Bookings ({filteredBookings.length})
          </h1>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 border border-[#E2E8F0] rounded-lg mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-grow relative">
              <Input
                type="text"
                placeholder="Search bookings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10"
                data-testid="bookings-search"
              />
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input w-full md:w-48"
              data-testid="bookings-status-filter"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* Bookings Grid */}
        {filteredBookings.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBookings.map((booking, index) => (
              <div 
                key={booking.booking_id || index}
                className="bg-white p-6 border border-[#E2E8F0] rounded-lg"
                data-testid={`booking-card-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-bold text-[#0A0F1C]">{booking.name}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(booking.status)}`}>
                      {booking.status}
                    </span>
                  </div>
                  <span className="text-sm text-[#475569]">{booking.service_type}</span>
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-[#475569]">
                    <Mail size={14} />
                    {booking.email}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#475569]">
                    <Phone size={14} />
                    {booking.phone}
                  </div>
                  {booking.preferred_date && (
                    <div className="flex items-center gap-2 text-sm text-[#475569]">
                      <Calendar size={14} />
                      {booking.preferred_date}
                    </div>
                  )}
                  {booking.preferred_time && (
                    <div className="flex items-center gap-2 text-sm text-[#475569]">
                      <Clock size={14} />
                      {booking.preferred_time}
                    </div>
                  )}
                </div>
                
                {booking.message && (
                  <p className="text-sm text-[#475569] mb-4 p-3 bg-[#F9FAFB] rounded">
                    {booking.message}
                  </p>
                )}
                
                {booking.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => updateBookingStatus(booking.booking_id, 'confirmed')}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm py-2"
                      data-testid={`confirm-booking-${index}`}
                    >
                      <CheckCircle size={16} className="mr-1" />
                      Confirm
                    </Button>
                    <Button
                      onClick={() => updateBookingStatus(booking.booking_id, 'cancelled')}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm py-2"
                      data-testid={`cancel-booking-${index}`}
                    >
                      <XCircle size={16} className="mr-1" />
                      Cancel
                    </Button>
                  </div>
                )}
                
                {booking.status === 'confirmed' && (
                  <Button
                    onClick={() => updateBookingStatus(booking.booking_id, 'completed')}
                    className="w-full bg-[#1D4ED8] hover:bg-[#2563EB] text-white text-sm py-2"
                    data-testid={`complete-booking-${index}`}
                  >
                    Mark Completed
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white p-8 border border-[#E2E8F0] rounded-lg text-center">
            <Calendar size={48} className="text-[#E2E8F0] mx-auto mb-4" />
            <p className="text-[#475569]">No bookings found</p>
          </div>
        )}
      </div></AdminLayout>);}
